import { useToken } from "@gluestack-style/react";
import Section from "components/general/Section";
import { Box, Button, HStack, Pressable, ScrollView, Text } from "components/ui";
import { CircleCheck } from "lucide-react-native";
import React from "react";
import { Notification, useGetNotificationQuery, usePostNotificationMarkreadMutation } from "store/abcoApi";
import { useIsMobile } from "utils/hooks/useIsMobile";
import NotificationFilter from "./NotificationFilter";

type Props = {
	dataList: Notification[];
	onEventTypeChange: (eventTypeIdCollection: number[]) => void;
	onHasReadChange: (hadRead: boolean | undefined) => void;
	onSiteChange: (siteIdCollection: number[]) => void;
	isDashboard?: boolean;
};

const tabDefinitions = [
	{ title: "ALL", eventTypes: [], eventTypeIds: [] },
	{ title: "Approvals", eventTypes: ["CartWaitingForApproval", "CartApproved", "CartRejected"], eventTypeIds: [1, 2, 3] },
	{ title: "Deliveries", eventTypes: ["ShippingInProgress", "ShippingInTransit", "ShippingCompleted", "ShippingFailure"], eventTypeIds: [4, 5, 6, 7] },
	{ title: "Service bookings", eventTypes: ["ServiceBooking"], eventTypeIds: [99999] },
	{ title: "Orders", eventTypes: ["OrderConfirmation"], eventTypeIds: [101] },
	{ title: "Invoices", eventTypes: ["InvoiceCreated"], eventTypeIds: [102] },
];

const NotificationHeader = ({ dataList, onEventTypeChange, onHasReadChange, onSiteChange, isDashboard = false }: Props) => {
	const [notificationMarkRead] = usePostNotificationMarkreadMutation();
	const isMobile = useIsMobile();
	const resolvedPrimarylight = useToken("colors", "primarylight");
	const [isMobileFilterOpen, setIsMobileFilterOpen] = React.useState(false);
	const [activeTabTitle, setActiveTabTitle] = React.useState("ALL");
	const { data } = useGetNotificationQuery({
		pageSize: 1,
		pageNumber: 1,
		hasRead: false,
	});

	let maxPageSize = data ? Math.ceil(data?.metaData.totalItemCount / 10) * 10 : 1;

	const { data: allData } = useGetNotificationQuery({
		hasRead: false,
		pageSize: maxPageSize,
	});

	const TabCollection = tabDefinitions.map((tab) => {
		if (tab.title === "ALL") {
			return {
				title: tab.title,
				eventTypeIds: tab.eventTypeIds,
				notificationCount: allData ? allData.metaData.totalItemCount : 0,
			};
		} else if (tab.title === "Approvals") {
			return {
				title: tab.title,
				eventTypeIds: tab.eventTypeIds,
				notificationCount: allData ? allData.list?.filter((x) => tab.eventTypes.includes(x.eventType!)).length : 0,
			};
		} else if (tab.title === "Deliveries") {
			return {
				title: tab.title,
				eventTypeIds: tab.eventTypeIds,
				notificationCount: allData ? allData.list?.filter((x) => tab.eventTypes.includes(x.eventType!)).length : 0,
			};
		} else if (tab.title === "Service bookings") {
			return {
				title: tab.title,
				eventTypeIds: tab.eventTypeIds,
				notificationCount: allData ? allData.list?.filter((x) => tab.eventTypes.includes(x.eventType!)).length : 0,
			};
		} else if (tab.title === "Orders") {
			return {
				title: tab.title,
				eventTypeIds: tab.eventTypeIds,
				notificationCount: allData ? allData.list?.filter((x) => tab.eventTypes.includes(x.eventType!)).length : 0,
			};
		} else if (tab.title === "Invoices") {
			return {
				title: tab.title,
				eventTypeIds: tab.eventTypeIds,
				notificationCount: allData ? allData.list?.filter((x) => tab.eventTypes.includes(x.eventType!)).length : 0,
			};
		}

		return {
			title: tab.title,
			eventTypeIds: tab.eventTypeIds,
			notificationCount: tab.eventTypes
				? tab.eventTypes.length === 0
					? dataList.filter((notification: any) => !notification.hasRead).length
					: dataList.filter((notification: any) => tab.eventTypes.includes(notification.eventType) && !notification.hasRead).length
				: 0,
		};
	});

	const handleMarkAllRead = () => {
		notificationMarkRead({ hasRead: false, pageSize: 999 }).then((response) => {});
	};

	const handleTabClick = (tab: any) => {
		setActiveTabTitle(tab.title);
		onEventTypeChange(tab.eventTypeIds);
	};

	const handleOnReadChange = (hasRead: boolean | undefined) => {
		onHasReadChange(hasRead);
	};

	const handleOnSiteChange = (site: any) => {
		onSiteChange([site.id]);
	};

	const NotificationLink = (data: any) => {
		const isActiveTab = data.tabData.title === activeTabTitle;
		const textColour = isActiveTab ? "$primarylight" : "$neutral600";
		const fontWeight = isActiveTab ? 700 : 400;
		const borderBottom = isActiveTab ? 2 : 0;
		return (
			<Pressable onPress={() => handleTabClick(data.tabData)} key={data.title} sx={{ flex: 1 }}>
				<Box
					sx={{
						backgroundColor: "$basicwhite",
						paddingVertical: 12,
						paddingHorizontal: isMobile ? 16 : 12,
						marginRight: 10,
						borderBottomColor: "$primarylight",
						borderBottomWidth: borderBottom,
					}}
					position="relative"
				>
					<HStack gap={8} alignItems="center" justifyContent="center">
						<Text sx={{ color: textColour, fontWeight: fontWeight, textAlign: "center" }}>{data.tabData.title}</Text>
						{data.tabData.notificationCount > 0 && (
							<Box justifyContent="center" alignItems="center" bg="$errormain" width={24} height={24} borderRadius={"$full"}>
								<Text color="$basicwhite">{data.tabData.notificationCount}</Text>
							</Box>
						)}
					</HStack>
				</Box>
			</Pressable>
		);
	};
	return (
		<>
			{isMobile && (
				<Box marginVertical={0} paddingHorizontal={12}>
					{/* <Button onPress={() => setIsMobileFilterOpen(true)} padding={12} bg="$basicwhite" borderWidth={1} borderColor="$primarylight" gap={8}>
						<Text color="$primarylight" fontWeight={"bold"}>
							Show Filters
						</Text>
						<ChevronDown color="#2B6FBB" />
					</Button> */}
					<NotificationFilter onHasReadChange={handleOnReadChange} onSiteChange={handleOnSiteChange} />
				</Box>
			)}
			<Section style={{ paddingBottom: 0, paddingHorizontal: isDashboard ? 0 : "auto" }}>
				{!isMobile && <NotificationFilter onHasReadChange={handleOnReadChange} onSiteChange={handleOnSiteChange} />}
				<Box bg="$neutral25" paddingVertical={32} paddingHorizontal={12}>
					{!isMobile && (
						<Box flexDirection="row">
							{TabCollection.map((child: any, index: number) => {
								return <NotificationLink key={index} tabData={child} />;
							})}
							<Pressable style={{ flexDirection: "row", justifyContent: "space-between" }} onPress={handleMarkAllRead}>
								<Box
									sx={{
										backgroundColor: "$basicwhite",
										paddingVertical: 0,
										paddingHorizontal: isMobile ? 16 : 12,
										marginRight: 0,
										justifyContent: "center",
										borderRadius: 8,
										borderColor: resolvedPrimarylight,
										borderWidth: 1,
										height: 44,
									}}
								>
									<HStack gap={4} alignItems="center">
										<CircleCheck size={18} color={resolvedPrimarylight} />
										<Text color={resolvedPrimarylight} sx={{ fontWeight: "bold" }}>
											{" "}
											Mark all as read{" "}
										</Text>
									</HStack>
								</Box>
							</Pressable>
						</Box>
					)}
					{isMobile && (
						<>
							<ScrollView horizontal>
								<HStack>
									{TabCollection.map((child: any, index: number) => (
										<NotificationLink key={index} tabData={child} />
									))}
								</HStack>
							</ScrollView>
							<Box>
								<Button marginTop={24} ml="auto" variant="outline" onPress={handleMarkAllRead}>
									<HStack gap={4} alignItems="center">
										<CircleCheck size={18} color={resolvedPrimarylight} />
										<Text color={resolvedPrimarylight} sx={{ fontWeight: "bold" }}>
											{" "}
											Mark all as read{" "}
										</Text>
									</HStack>
								</Button>
							</Box>
						</>
					)}
				</Box>
			</Section>
		</>
	);
};

export default NotificationHeader;
